import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { DISTRICTS, LOCAL_BODY_TYPES } from "@/data/static";
import { formatDateDMY } from "@/lib/dates";
import { Plus, Receipt, IndianRupee, Upload, Eye, Download } from "lucide-react";
import FileViewer from "@/components/FileViewer";

const STATUS = {
  pending: { label: "Pending", cls: "bg-yellow-500/15 text-yellow-700" },
  approved: { label: "Approved", cls: "bg-blue-500/15 text-blue-700" },
  paid: { label: "Paid", cls: "bg-brand-green/15 text-brand-green" },
  rejected: { label: "Rejected", cls: "bg-red-500/15 text-red-600" },
};

export default function ExpenseClaimPanel() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [options, setOptions] = useState({ committee_positions: [], categories: [], payment_methods: [] });
  const [viewing, setViewing] = useState(null);

  const load = () => api.get("/expense-claims/mine").then(r => setItems(r.data)).catch(() => {});

  useEffect(() => {
    load();
    api.get("/expense-claims/options").then(r => setOptions(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-brand-blue" />
          <div className="font-bold">Expense Reimbursement Claims</div>
        </div>
        <ClaimDialog user={user} options={options} onSubmitted={load} />
      </div>
      <Card>
        <ul className="divide-y divide-border">
          {items.length === 0 && (
            <li className="p-8 text-center text-sm text-muted-foreground">
              No claims yet. Click "New Claim" to submit one.
            </li>
          )}
          {items.map(c => {
            const s = STATUS[c.status] || STATUS.pending;
            return (
              <li key={c.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${s.cls}`}>{s.label}</span>
                    <Badge variant="outline" className="text-[10px]">{c.category}</Badge>
                    <span className="text-xs text-muted-foreground">{formatDateDMY(c.date_of_expense)}</span>
                  </div>
                  <div className="font-semibold text-sm truncate">{c.description}</div>
                  <div className="text-xs text-muted-foreground">
                    {c.committee_position} • {c.district} • {c.payment_method}
                  </div>
                  {c.note && <div className="text-xs text-muted-foreground mt-1">Reviewer note: {c.note}</div>}
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-mono font-bold text-lg">₹{Number(c.amount).toLocaleString("en-IN")}</div>
                    {c.status === "paid" && c.payment_reference && (
                      <div className="text-[10px] text-muted-foreground">Ref: {c.payment_reference}</div>
                    )}
                  </div>
                  {c.bill_url && (
                    <Button size="sm" variant="outline" onClick={() => setViewing(c)} data-testid={`claim-view-${c.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> Bill
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </Card>
      <FileViewer
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={viewing ? `Bill — ${viewing.description}` : ""}
        url={viewing?.bill_url}
      />
    </div>
  );
}

function ClaimDialog({ user, options, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const initial = () => ({
    full_name: user?.full_name || "",
    email: user?.email || "",
    phone: user?.mobile || "",
    district: user?.district || "Thiruvananthapuram",
    local_body_type: user?.local_body_type || "Grama Panchayat",
    local_body_name: user?.local_body_name || "",
    committee_position: "Not a Committee Member",
    date_of_expense: new Date().toISOString().slice(0, 10),
    category: "Travel - Bus",
    description: "",
    amount: "",
    payment_method: "UPI",
    more_details: "",
    bill_url: "",
    account_holder_name: user?.full_name || "",
    account_number: "",
    ifsc_code: "",
    bank_name: "",
    upi_id: "",
  });
  const [form, setForm] = useState(initial);
  const [fileName, setFileName] = useState("");
  const fileRef = React.useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onFile = (file) => {
    if (!file) return;
    const ok = file.type === "application/pdf" || file.type.startsWith("image/");
    if (!ok) { toast.error("PDF or image only"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { set("bill_url", ev.target.result); setFileName(file.name); };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount || Number(form.amount) <= 0) {
      toast.error("Description and amount required"); return;
    }
    if (!form.upi_id && !form.account_number) {
      toast.error("Provide UPI ID or bank account for reimbursement"); return;
    }
    try {
      setBusy(true);
      await api.post("/expense-claims", { ...form, amount: Number(form.amount) });
      toast.success("Claim submitted for treasurer review");
      setOpen(false);
      setForm(initial());
      setFileName("");
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(initial()); setFileName(""); } }}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-brand-blue text-white hover:bg-brand-blue/90" data-testid="claim-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New Claim
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-brand-green" /> Expense Reimbursement Claim
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <section>
            <div className="text-xs uppercase tracking-widest text-brand-blue font-semibold mb-2">Your Details</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Full Name"><Input value={form.full_name} onChange={e => set("full_name", e.target.value)} required /></Field>
              <Field label="Email"><Input type="email" value={form.email} onChange={e => set("email", e.target.value)} required /></Field>
              <Field label="Phone Number"><Input value={form.phone} onChange={e => set("phone", e.target.value)} required /></Field>
              <Field label="Committee Position">
                <Select value={form.committee_position} onValueChange={v => set("committee_position", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {options.committee_positions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="District">
                <Select value={form.district} onValueChange={v => set("district", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DISTRICTS.map(d => <SelectItem key={d.slug} value={d.name}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Local Body Type">
                <Select value={form.local_body_type} onValueChange={v => set("local_body_type", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LOCAL_BODY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Local Body Name" full><Input value={form.local_body_name} onChange={e => set("local_body_name", e.target.value)} placeholder="e.g., Vyttila Grama Panchayat" /></Field>
            </div>
          </section>

          <section>
            <div className="text-xs uppercase tracking-widest text-brand-blue font-semibold mb-2">Expense Details</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Date of Expense"><Input type="date" value={form.date_of_expense} onChange={e => set("date_of_expense", e.target.value)} required /></Field>
              <Field label="Category">
                <Select value={form.category} onValueChange={v => set("category", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {options.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Amount Spent (₹)"><Input type="number" min="1" step="0.01" value={form.amount} onChange={e => set("amount", e.target.value)} required /></Field>
              <Field label="Payment Method">
                <Select value={form.payment_method} onValueChange={v => set("payment_method", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {options.payment_methods.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Description / Purpose" full><Input value={form.description} onChange={e => set("description", e.target.value)} placeholder="e.g., Travel to Kochi for state conference" required /></Field>
              <Field label="More Details (optional)" full><Textarea rows={2} value={form.more_details} onChange={e => set("more_details", e.target.value)} placeholder="Any additional context…" /></Field>
              <Field label="Upload Bill / Receipt / Invoice" full>
                <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={e => onFile(e.target.files?.[0])} />
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
                    <Upload className="w-4 h-4 mr-1.5" /> Choose File
                  </Button>
                  {fileName && <span className="text-xs text-muted-foreground truncate max-w-[220px]">{fileName}</span>}
                  {form.bill_url && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => { set("bill_url", ""); setFileName(""); }}>Remove</Button>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">PDF or image, max 5MB.</p>
              </Field>
            </div>
          </section>

          <section>
            <div className="text-xs uppercase tracking-widest text-brand-blue font-semibold mb-2">Reimbursement Payout — provide UPI ID or bank details</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="UPI ID"><Input value={form.upi_id} onChange={e => set("upi_id", e.target.value)} placeholder="9747xxxxxx@upi" /></Field>
              <Field label="Account Holder Name"><Input value={form.account_holder_name} onChange={e => set("account_holder_name", e.target.value)} /></Field>
              <Field label="Account Number"><Input value={form.account_number} onChange={e => set("account_number", e.target.value)} /></Field>
              <Field label="IFSC Code"><Input value={form.ifsc_code} onChange={e => set("ifsc_code", e.target.value)} placeholder="SBIN0001234" /></Field>
              <Field label="Bank Name" full><Input value={form.bank_name} onChange={e => set("bank_name", e.target.value)} placeholder="State Bank of India" /></Field>
            </div>
          </section>

          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand-green text-white hover:bg-brand-green/90" data-testid="claim-submit-btn">
              {busy ? "Submitting…" : "Submit Claim"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="mb-1.5 block text-xs">{label}</Label>
      {children}
    </div>
  );
}
